import React from 'react';
import {
    faRegularChartMixedTrex,
    faRegularScreenUsersGear,
    faRegularScrewdriverGear,
    faRegularSitemapGear,
    faRegularDisplayChartUpUser,
    faRegularBinaryMagnifyingGlass,
} from '@awesome.me/kit-2cbcf7d4a0/icons/kit/custom';
import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Constants from '@utils/constants';

export interface PageNavItem {
    key: string;
    label: string;
    admin?: number;
    auditAdmin?: boolean;
    element?: string;
    moduleValue?: number;
    orgAdmin?: boolean;
    showSiteStudy?: boolean;
    studyAdmin?: boolean;
    isSmStudyAdmin?: boolean;
    reportAdmin?: boolean;
    readOnly?: boolean;
    tooltip?: string;
    hidden?: boolean;
    exclude?: (number | null)[];
    searchActive?: boolean;
    expandOnSearch?: boolean;
    faIcon?: IconDefinition | IconProp;
    items?: PageNavItem[];
    params?: Record<string, string>;
    currentPageItemsInfo?: boolean;
}

// List of acceptable keys for DynamicRoutesList.
// Extend when adding new dynamic routes.
type DynamicRoutes = 'dataView';
type DynamicRoutesList = {
    [key in DynamicRoutes]: PageNavItem[];
};

export function getAllLeafNavItems(items: PageNavItem[]): PageNavItem[] {
    const leaves: PageNavItem[] = [];
    const stack = [...items];

    while (stack.length) {
        const item = stack.pop();
        if (item) {
            if (item.items && item.items.length) {
                stack.push(...item.items);
            } else {
                leaves.unshift(item);
            }
        }
    }

    return leaves;
}

export const pageNavItems: (dynamicItems?: DynamicRoutesList) => PageNavItem[] = (dynamicItems) => [
    {
        label: 'home',
        key: 'home',
        faIcon: ['far', 'home-alt'],
        tooltip: 'home',
    },
    {
        label: 'riskAssessment',
        key: 'risk-assessment-nav',
        faIcon: ['far', 'file-pen'],
        moduleValue: Constants.Modules.RAM,
        tooltip: 'riskAssessment',
        items: [
            {
                label: 'ram:ctqf:title',
                key: 'ra-critical-to-quality-factors',
                moduleValue: Constants.Modules.RAM,
                element: Constants.PageElements.CriticalToQualityFactors,
            },
            {
                label: 'ram:riskLog:title',
                key: 'ra-risk-log',
                moduleValue: Constants.Modules.RAM,
                element: Constants.PageElements.RiskLog,
            },
            {
                label: 'ram:riskMonitoring:title',
                key: 'ra-risk-monitoring',
                moduleValue: Constants.Modules.RAM,
                element: Constants.PageElements.RiskMonitoring,
            },
            {
                label: 'ra:riskDashboard',
                key: 'ra-risk-dashboard',
                moduleValue: Constants.Modules.RAM,
                element: Constants.PageElements.RiskDashboard,
            },
            {
                label: 'common:studyTeam',
                key: 'ra-study-team',
                moduleValue: Constants.Modules.RAM,
                element: Constants.PageElements.StudyTeam,
            },
            {
                label: 'ram:manageCmControls:title',
                key: 'ra-manage-cm-controls',
                moduleValue: Constants.Modules.RAM,
                element: Constants.PageElements.CMControls,
            },
            {
                label: 'ra:reporting',
                key: 'ra-reporting',
                moduleValue: Constants.Modules.RAM,
                element: Constants.PageElements.RiskReports,
                items: [
                    {
                        label: 'ra:reports',
                        key: 'ra-reports',
                        moduleValue: Constants.Modules.RAM,
                        element: Constants.PageElements.RiskReports,
                    },
                    {
                        label: 'ra:generationHistory',
                        key: 'ra-generation-history',
                        moduleValue: Constants.Modules.RAM,
                        element: Constants.PageElements.RiskReports,
                    },
                    {
                        label: 'ra:manageSignatories',
                        key: 'ra-manage-signatories',
                        moduleValue: Constants.Modules.RAM,
                        element: Constants.PageElements.RiskReports,
                    },
                    {
                        label: 'admin:excelListings',
                        key: 'ra-excel-listing',
                        moduleValue: Constants.Modules.RAM,
                        element: Constants.PageElements.RiskReports,
                    },
                ],
            },
        ],
    },
    {
        label: 'centralMonitoring',
        key: 'central-monitoring-nav',
        faIcon: ['far', 'chart-mixed'],
        moduleValue: Constants.Modules.CM2,
        tooltip: 'centralMonitoring',
        items: [
            {
                label: 'cm:controlsHub.title',
                key: 'cm2-controls-hub',
                moduleValue: Constants.Modules.CM2,
                element: Constants.PageElements.ControlsHubCM2,
            },
            {
                label: 'cm:studyHub.title',
                key: 'cm2-study-hub',
                moduleValue: Constants.Modules.CM2,
                element: Constants.PageElements.StudyHubCM2,
            },
            {
                label: 'cm:siteHub.title',
                key: 'cm2-site-hub',
                moduleValue: Constants.Modules.CM2,
                element: Constants.PageElements.SiteHubCM2,
            },
            {
                label: 'cm:datasetHub.title',
                key: 'cm2-dataset-hub',
                moduleValue: Constants.Modules.CM2,
                element: Constants.PageElements.DatasetHubCM2,
            },
            {
                label: 'cm:dataViews',
                key: 'cm2-data-views',
                moduleValue: Constants.Modules.CM2,
                element: Constants.PageElements.DataViewsCM2,
                items: dynamicItems?.dataView.length ? dynamicItems?.dataView : undefined,
                readOnly: !dynamicItems?.dataView.length,
                suffixIcon: !dynamicItems?.dataView.length ? (
                    <FontAwesomeIcon icon={['fal', 'circle-exclamation']} transform="180deg" fontSize={16} />
                ) : undefined,
                tooltip: !dynamicItems?.dataView.length ? 'common:PageNavTooltips:NoDataViews' : undefined,
            },
            {
                label: 'cm:studyTeam',
                key: 'cm2-study-team',
                moduleValue: Constants.Modules.CM2,
                element: Constants.PageElements.StudyTeamCM2,
            },
            {
                label: 'cm:reporting',
                key: 'cm2-reporting',
                moduleValue: Constants.Modules.CM2,
                element: Constants.PageElements.CM2Reports,
                items: [
                    {
                        label: 'cm:reports',
                        key: 'cm2-reports',
                        moduleValue: Constants.Modules.CM2,
                        element: Constants.PageElements.CM2Reports,
                    },
                    {
                        label: 'cm:generationHistory',
                        key: 'cm2-generation-history',
                        moduleValue: Constants.Modules.CM2,
                        element: Constants.PageElements.CM2Reports,
                    },
                    {
                        label: 'cm:manageSignatories',
                        key: 'cm2-manage-signatories',
                        moduleValue: Constants.Modules.CM2,
                        element: Constants.PageElements.CM2Reports,
                    },
                ],
            },
        ],
    },
    {
        label: 'legacyCentralMonitoring',
        key: 'legacy-central-monitoring-nav',
        faIcon: faRegularChartMixedTrex as IconProp,
        moduleValue: Constants.Modules.CM,
        tooltip: 'legacyCentralMonitoring',
        items: [
            {
                label: 'cm:studyDashboard',
                key: 'cm-study-dashboard',
                moduleValue: Constants.Modules.CM,
                element: Constants.PageElements.StudyDashboard,
            },
            {
                label: 'cm:siteDashboard',
                key: 'cm-site-dashboard',
                moduleValue: Constants.Modules.CM,
                element: Constants.PageElements.SiteDashboard,
            },
            {
                label: 'cm:ObservationsAndActions',
                key: 'cm-observations-and-actions',
                items: [
                    {
                        label: 'cm:manageObservations',
                        key: 'cm-manage-observations',
                        moduleValue: Constants.Modules.CM,
                        element: Constants.PageElements.CMObservations,
                    },
                    {
                        label: 'cm:manageActions',
                        key: 'cm-manage-actions',
                        moduleValue: Constants.Modules.CM,
                        element: Constants.PageElements.CMActions,
                    },
                ],
            },
            {
                label: 'cm:studyTeam',
                key: 'cm-study-team',
                moduleValue: Constants.Modules.CM,
                element: Constants.PageElements.CMStudyTeam,
            },
            {
                label: 'LegacyReports',
                key: 'cm-legacy-reports',
                moduleValue: Constants.Modules.CM,
                element: Constants.PageElements.CMReports,
            },
            {
                label: 'cm:reporting',
                key: 'cm-reporting',
                moduleValue: Constants.Modules.CM,
                element: Constants.PageElements.CMReports,
                items: [
                    {
                        label: 'cm:reports',
                        key: 'cm-reports',
                        moduleValue: Constants.Modules.CM,
                        element: Constants.PageElements.CMReports,
                    },
                    {
                        label: 'cm:generationHistory',
                        key: 'cm-generation-history',
                        moduleValue: Constants.Modules.CM,
                        element: Constants.PageElements.CMReports,
                    },
                    {
                        label: 'cm:manageSignatories',
                        key: 'cm-manage-signatories',
                        moduleValue: Constants.Modules.CM,
                        element: Constants.PageElements.CMReports,
                    },
                ],
            },
        ],
    },
    {
        label: 'subjectMonitoring',
        key: 'subject-monitoring-nav',
        faIcon: faRegularDisplayChartUpUser as IconProp,
        moduleValue: Constants.Modules.SM,
        tooltip: 'subjectMonitoring',
        items: [
            {
                label: 'sm:subjectHub.title',
                key: 'sm-subject-hub',
                moduleValue: Constants.Modules.SM,
                element: Constants.PageElements.SubjectHubSM,
            },
            {
                label: 'sm:subjectProfile.title',
                key: 'sm-subject-profile',
                moduleValue: Constants.Modules.SM,
                element: Constants.PageElements.SubjectProfileSM,
            },
            {
                label: 'sm:studyTeam',
                key: 'sm-study-team',
                moduleValue: Constants.Modules.SM,
                element: Constants.PageElements.SubjectMonitoringStudyTeam,
            },
            {
                label: 'sm:reporting',
                key: 'sm-reporting',
                moduleValue: Constants.Modules.SM,
                element: Constants.PageElements.SubjectMonitoringReports,
                items: [
                    {
                        label: 'sm:reports',
                        key: 'sm-reports',
                        moduleValue: Constants.Modules.SM,
                        element: Constants.PageElements.SubjectMonitoringReports,
                    },
                    {
                        label: 'sm:generationHistory',
                        key: 'sm-generation-history',
                        moduleValue: Constants.Modules.SM,
                        element: Constants.PageElements.SubjectMonitoringReports,
                    },
                    {
                        label: 'sm:manageSignatories',
                        key: 'sm-manage-signatories',
                        moduleValue: Constants.Modules.SM,
                        element: Constants.PageElements.SubjectMonitoringReports,
                    },
                ],
            },
            {
                label: 'sm:manageReviewPlans',
                key: 'sm-manage-review-plans',
                moduleValue: Constants.Modules.SM,
                element: Constants.PageElements.SubjectMonitoringManageReviewPlans,
            },
        ],
    },
    {
        label: 'dataQuality',
        key: 'data-quality-nav',
        faIcon: faRegularBinaryMagnifyingGlass as IconProp,
        tooltip: 'dataQuality',
        moduleValue: Constants.Modules.DQ,
        element: Constants.PageElements.DataQuality,
    },
    {
        label: 'systemAdmin',
        key: 'system-admin',
        faIcon: faRegularScrewdriverGear as IconProp,
        exclude: [Constants.SystemAdminLevel.Contributor],
        tooltip: 'systemAdmin',
        items: [
            {
                label: 'uam:manageOrganisations',
                key: 'admin-organisations',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageUsers',
                key: 'admin-users',
                items: [
                    {
                        label: 'admin:userAccounts',
                        key: 'admin-user-accounts',
                        showSiteStudy: false,
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                    {
                        label: 'admin:eventLog',
                        key: 'admin-event-log',
                        showSiteStudy: false,
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                ],
            },
            {
                label: 'admin:manageRiskLibrary',
                key: 'admin-risks',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageReportTemplates',
                key: 'admin-manage-report-templates',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageSystemPlugin',
                key: 'admin-system-plugin',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Administrator,
            },
            {
                label: 'admin:manageSnapshots',
                key: 'admin-system-snapshot',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.SysAdmin,
            },
        ],
    },
    {
        label: 'organisationAdmin',
        key: 'organisation-admin',
        faIcon: faRegularSitemapGear as IconProp,
        exclude: [Constants.SystemAdminLevel.Contributor],
        orgAdmin: true,
        auditAdmin: true,
        reportAdmin: true,
        tooltip: 'organisationAdmin',
        items: [
            {
                label: 'admin:manageUAM',
                key: 'admin-org-roles',
                showSiteStudy: false,
                orgAdmin: true,
            },
            {
                label: 'admin:manageUsers',
                key: 'admin-org-users',
                items: [
                    {
                        label: 'admin:userAccounts',
                        key: 'admin-org-user-accounts',
                        showSiteStudy: false,
                        orgAdmin: true,
                    },
                    {
                        label: 'admin:eventLog',
                        key: 'admin-org-event-log',
                        showSiteStudy: false,
                        orgAdmin: true,
                    },
                ],
            },
            {
                label: 'admin:manageReports',
                key: 'admin-org-manage-reports',
                items: [
                    {
                        label: 'admin:reportTemplates',
                        key: 'admin-org-report-templates',
                        showSiteStudy: false,
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                    {
                        label: 'admin:excelListings',
                        key: 'admin-org-excel-listing',
                        showSiteStudy: false,
                        reportAdmin: true,
                    },
                ],
            },
            {
                label: 'admin:manageRam:mainNav',
                key: 'admin-org-manage-ram',
                items: [
                    {
                        label: 'admin:manageRam:navDashboard',
                        key: 'admin-org-ram-dashboard',
                        showSiteStudy: false,
                        reportAdmin: true,
                    },
                ],
            },
            {
                label: 'admin:manageStudies',
                key: 'admin-org-studies',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageLOVs',
                key: 'admin-org-lovs',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageEvaluationModels',
                key: 'manage-evaluation-models',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageRiskLibrary',
                key: 'org-risk-library',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageProducts',
                key: 'admin-org-products',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageIndications',
                key: 'admin-org-indications',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:managePrograms',
                key: 'admin-org-programs',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageOrgPlugin',
                key: 'admin-organisation-plugin',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Administrator,
            },
            {
                label: 'admin:manageCMIntegration.mainNav',
                key: 'admin-org-manage-cm-integration',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
                exclude: [Constants.SystemAdminLevel.Contributor],
            },
            {
                label: 'admin:auditLog',
                key: 'audit-log',
                showSiteStudy: false,
                auditAdmin: true,
            },
            {
                label: 'admin:smSubjectMonitoring',
                key: 'admin-org-sm-nav',
                showSiteStudy: false,
                admin: Constants.SystemAdminLevel.Reviewer,
                exclude: [Constants.SystemAdminLevel.Contributor],
                items: [
                    {
                        label: 'admin:smManageReviews',
                        key: 'admin-org-sm-manage-reviews',
                        showSiteStudy: false,
                        admin: Constants.SystemAdminLevel.Reviewer,
                        exclude: [Constants.SystemAdminLevel.Contributor],
                    },
                ],
            },
        ],
    },
    {
        label: 'studyAdmin',
        key: 'study-administration',
        faIcon: faRegularScreenUsersGear as IconProp,
        exclude: [Constants.SystemAdminLevel.Contributor],
        tooltip: 'studyAdmin',
        items: [
            { label: 'admin:manageStudyTeam', key: 'manage-study-members', studyAdmin: true },
            { label: 'admin:manageSmStudyTeam', key: 'manage-sm-study-members', isSmStudyAdmin: true },
            {
                label: 'admin:dataProcessingLog',
                key: 'data-processing-log',
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            { label: 'admin:manageStudy', key: 'manage-study-settings', admin: Constants.SystemAdminLevel.Reviewer },
            {
                label: 'admin:manageHubs',
                key: 'manage-hubs',
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageVisualizations',
                key: 'manage-visualisations',
                items: [
                    {
                        label: 'admin:managePanels.pageTitle',
                        key: 'manage-panels',
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                    {
                        label: 'admin:manageDashboardPanels',
                        key: 'manage-study-panels',
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                ],
            },
            {
                label: 'admin:manageDataTables',
                key: 'manage-study-data-review',
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageDataViews',
                key: 'manage-data-views',
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageLegacyCM',
                key: 'manage-legacy-CM',
                admin: Constants.SystemAdminLevel.Reviewer,
                items: [
                    {
                        label: 'admin:manageStudyDashboard',
                        key: 'manage-study-dashboard',
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                    {
                        label: 'admin:manageSiteDashboard',
                        key: 'manage-site-dashboard',
                        showSiteStudy: true,
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                    {
                        label: 'admin:manageDatasets',
                        key: 'manage-study-datasets',
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                ],
            },
            {
                label: 'admin:manageSVTs',
                key: 'manage-study-visit-schedules',
                items: [
                    {
                        label: 'admin:visitSchedules',
                        key: 'manage-visit-schedules',
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                    { label: 'admin:manageCRFs', key: 'manage-study-crfs', admin: Constants.SystemAdminLevel.Reviewer },
                    {
                        label: 'admin:manageAssessments',
                        key: 'manage-study-assessments',
                        admin: Constants.SystemAdminLevel.Reviewer,
                    },
                ],
            },
            {
                label: 'admin:manageExtraData',
                key: 'manage-study-extra-data',
                admin: Constants.SystemAdminLevel.Reviewer,
            },
            {
                label: 'admin:manageStudyPlugin',
                key: 'admin-study-plugin',
                admin: Constants.SystemAdminLevel.Administrator,
            },
        ],
    },
    {
        label: 'common:dummyComponentsPage',
        key: 'dummy-components-page',
        faIcon: ['far', 'puzzle-piece'],
        tooltip: 'common:dummyComponentsPage',
        exclude: ![
            Constants.environment.LOCAL,
            Constants.environment.DEV,
            Constants.environment.DEV1,
            Constants.environment.DEV2,
            Constants.environment.TEST,
            Constants.environment.TEST2,
        ].includes(window.environment)
            ? [
                  Constants.SystemAdminLevel.None,
                  Constants.SystemAdminLevel.Reviewer,
                  Constants.SystemAdminLevel.Contributor,
                  Constants.SystemAdminLevel.Administrator,
                  Constants.SystemAdminLevel.SysAdmin,
              ]
            : [null],
    },
];
